import { Controller } from "@hotwired/stimulus"

// Comments on a document (assistant_documents/_comments). The pane is folded
// until the title menu ("Show all comments", by window event - the menu may sit
// in the side panel's head) or a section's own comment button opens it; adding,
// resolving, reopening and removing are each one request, after which the view
// reloads itself - the panel by asking itself to open the same document, the
// page by reloading - and the pane stays open across that reload.
const OPEN_KEY = "jane-document-comments-open"

export default class extends Controller {
    static targets = ["pane", "section", "body", "error"]
    static values = { url: String, panel: Boolean, panelUrl: String, title: String, key: String, askUrl: String, conversationId: Number }

    connect() {
        this._toggle = () => this._setOpen(this.paneTarget.hidden)
        window.addEventListener("document:toggle-comments", this._toggle)
        if (this._wasOpen()) this.paneTarget.hidden = false
    }

    disconnect() {
        window.removeEventListener("document:toggle-comments", this._toggle)
    }

    close() {
        this._setOpen(false)
    }

    // A section's comment button: open the pane aimed at that section.
    commentOn(event) {
        const index = event.currentTarget.dataset.sectionIndex
        this._setOpen(true)
        if (this.hasSectionTarget) this.sectionTarget.value = index
        this.hasBodyTarget && this.bodyTarget.focus()
        this.paneTarget.scrollIntoView({ block: "nearest", behavior: "smooth" })
    }

    async submit(event) {
        event.preventDefault()
        const body = this.bodyTarget.value.trim()
        if (!body) return this._error("Write a comment first.")

        this._error(null)
        const section = this.sectionTarget.value
        const ok = await this._request(this.urlValue, "POST", { body, section_index: section === "" ? null : Number(section) })
        if (ok) this._reload()
    }

    async resolve(event) { await this._act(event, "PATCH", { resolved: true }) }
    async reopen(event) { await this._act(event, "PATCH", { resolved: false }) }

    async remove(event) {
        if (!window.confirm("Delete this comment?")) return

        await this._act(event, "DELETE")
    }

    // Hand the comment to Jane in the chat the document was drafted in; she
    // reads the document and saves a revision, which shows up here as a new version.
    async askJane(event) {
        const button = event.currentTarget
        if (!this.askUrlValue || !this.conversationIdValue) return

        button.disabled = true
        const data = new FormData()
        data.append("message", button.dataset.askText)
        data.append("conversation_id", this.conversationIdValue)
        try {
            const response = await fetch(this.askUrlValue, {
                method: "POST", credentials: "same-origin", body: data,
                headers: { Accept: "application/json", "X-CSRF-Token": this._csrf() }
            })
            button.textContent = response.ok ? "Asked - see the chat" : "Couldn't ask"
        } catch (_error) {
            button.textContent = "Couldn't ask"
            button.disabled = false
        }
    }

    async _act(event, method, body) {
        const id = event.currentTarget.dataset.commentId
        const ok = await this._request(`${this.urlValue}/${id}`, method, body)
        if (ok) this._reload()
    }

    async _request(url, method, body) {
        try {
            const response = await fetch(url, {
                method, credentials: "same-origin",
                headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this._csrf() },
                body: body ? JSON.stringify(body) : undefined
            })
            if (response.ok) return true

            const data = await response.json().catch(() => ({}))
            this._error(data.error || "That didn't work. Try again.")
        } catch (_error) {
            this._error("That didn't work. Check your connection and try again.")
        }
        return false
    }

    _reload() {
        this._remember(true)
        if (this.panelValue) {
            window.dispatchEvent(new CustomEvent("jane:open-panel", {
                detail: { url: this.panelUrlValue, title: this.titleValue, icon: "bi-file-earmark-word", label: "Document" }
            }))
        } else {
            window.location.reload()
        }
    }

    _setOpen(open) {
        this.paneTarget.hidden = !open
        this._remember(open)
    }

    _wasOpen() {
        try { return window.sessionStorage.getItem(`${OPEN_KEY}:${this.keyValue}`) === "1" } catch (_error) { return false }
    }

    _remember(open) {
        try { window.sessionStorage.setItem(`${OPEN_KEY}:${this.keyValue}`, open ? "1" : "0") } catch (_error) { /* no storage: it just starts folded */ }
    }

    _error(message) {
        if (!this.hasErrorTarget) return

        this.errorTarget.textContent = message || ""
        this.errorTarget.hidden = !message
    }

    _csrf() {
        return document.querySelector("meta[name='csrf-token']")?.content || ""
    }
}
